import Box from '@/common/components/Box'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Select from '@/common/components/Select'
import { UnitTextField } from '@/common/components/UnitTextField'
import { filterOptions } from '@/common/constants/filterOptions.constant'
import { unities, unitiesDecimals } from '@/common/constants/unities.constants'
import useDebounce from '@/common/hooks/useDebounce'
import { Option } from '@/common/types/option'
import { cn } from '@/common/utils/cn'
import { formatDecimalUnit } from '@/common/utils/formatters'
import { unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/companyBranches/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/companyBranches/services/allowedCategoriesAndProductsTransformer'
import { ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData } from '@/features/companyBranches/types/allowedCategoriesAndProducts.type'
import { EntryProductFormItemQuantity } from '@/features/governmentAllocations/pages/Update/components/EntryProductFormItemQuantity'
import { GovernmentAllocationEntryProductCategoryQuantityValidationSchema } from '@/features/governmentAllocations/schemas/update.schema'
import { GovernmentAllocationEntryProduct, GovernmentAllocationEntryProductCategory } from '@/features/governmentAllocations/types/governmentAllocationEntry.type'
import { BoxesIcon, SearchIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { FilterOptionOption } from 'react-select'

interface EntryProductCategoryQuantityProps {
  companyBranchId?: string
  entryProductCategory: GovernmentAllocationEntryProductCategory
  allowedCategorySummary: ClientCompanyAllowedCategoryAndProductData
  disabled: boolean
  onTotalValueChange?: () => void
  governmentAllocationCurrencyRestricted: boolean | undefined
}

export const EntryProductCategoryQuantity = ({
  companyBranchId,
  entryProductCategory,
  allowedCategorySummary,
  disabled,
  onTotalValueChange,
  governmentAllocationCurrencyRestricted,
}: EntryProductCategoryQuantityProps) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<GovernmentAllocationEntryProductCategoryQuantityValidationSchema>()

  const [searchProduct, setSearchProduct] = useDebounce('')

  const { data: allowedCategoriesAndProducts, isPending: isAllowedCategoriesAndProductsPending } =
    useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
      companyBranchId,
      filters: {
        search: searchProduct,
        productCategoryId: entryProductCategory?.product_category?.id,
      },
    })

  const allowedProductsOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending ||
      !allowedCategoriesAndProducts ||
      !allowedCategoriesAndProducts['products']
      ? []
      : ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
        key: 'products',
        controlledBy: CompanyContractConsumptionControlledBy.Quantity,
      })(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending])

  const {
    fields: entryProducts,
    append: appendEntryProduct,
    remove: removeEntryProduct,
  } = useFieldArray({
    control,
    name: 'entry_products',
  })

  const watchCurrentCategoryGrantedQuantity = useWatch({
    control,
    name: 'total_quantity',
  })

  const availableQuantity = Number(allowedCategorySummary?.available_quantity) || 0
  const availableForAllocation = Math.max(
    availableQuantity - Number(unFormatUnknownUnit(watchCurrentCategoryGrantedQuantity || '') || 0),
    0,
  )

  const productCategoryUnit = useMemo(() => {
    const firstProductUnit = (entryProducts || [])[0]?.product.unit_for_quantity;

    return (entryProducts || []).every(p => p.product.unit_for_quantity === firstProductUnit)
      ? firstProductUnit
      : undefined;
  }, [(entryProducts || []).length]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2">
          <BoxesIcon strokeWidth={1} />

          <p>{entryProductCategory?.product_category?.display_name}</p>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <UnitTextField
          label="Quantidade total do serviço"
          unit={productCategoryUnit ? unities[productCategoryUnit] : undefined}
          decimals={productCategoryUnit ? unitiesDecimals[productCategoryUnit] : 2}
          maxValue={999_999_999.99}
          autoCorrect="off"
          {...register('total_quantity')}
          onChange={(event) => {
            register('total_quantity').onChange(event)
            onTotalValueChange?.()
          }}
          value={watch('total_quantity')}
          forceValue
          markAsRequired
          errorMessage={errors.total_quantity?.message || undefined}
          disabled={disabled || entryProducts?.length > 0}
        />

        {allowedCategorySummary && (
          <div className="flex gap-3">
            <p className="italic text-xs text-white-400">
              Quantidade possível:{' '}
              {allowedCategorySummary?.available_quantity
                ? formatDecimalUnit(Number(allowedCategorySummary?.available_quantity), productCategoryUnit)
                : '--'}
            </p>

            <p className="italic text-xs text-white-400">
              Disponível para distribuir: {formatDecimalUnit(Number(availableForAllocation), productCategoryUnit)}
            </p>

            <p className="italic text-xs text-white-400">
              Quantidade garantida:{' '}
              {allowedCategorySummary?.granted_quantity
                ? formatDecimalUnit(Number(allowedCategorySummary?.granted_quantity), productCategoryUnit)
                : '--'}
            </p>

            <p className="italic text-xs text-white-400">
              Quantidade inserida:{' '}
              {allowedCategorySummary?.entry_quantity
                ? formatDecimalUnit(Number(allowedCategorySummary?.entry_quantity), productCategoryUnit)
                : '--'}
            </p>
          </div>
        )}
      </div>

      {governmentAllocationCurrencyRestricted && (
        <div className='flex flex-col gap-1'>
          <CurrencyTextField
            label="Valor total do serviço"
            {...register('total_value')}
            markAsRequired
            forceValue
            value={watch('total_value')}
            errorMessage={errors.total_value?.message || undefined}
            disabled={disabled || entryProducts.length > 0}
          />
        </div>
      )}

      <Box className="space-y-4 p-4">
        <Select
          placeholder={
            <div className={cn('flex items-center gap-x-2', !disabled && 'text-white-800')}>
              <SearchIcon />
              <p>Pesquisar produtos</p>
            </div>
          }
          isLoading={isAllowedCategoriesAndProductsPending}
          options={
            allowedProductsOptions?.filter((productOption) => {
              return !entryProducts?.some(({ product }) => product?.id === productOption.value)
            }) || []
          }
          onInputChange={setSearchProduct}
          filterOption={
            filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean
          }
          value=""
          onChange={(newValue) => {
            appendEntryProduct({
              product: {
                id: (newValue as Option).value,
                display_name: (newValue as ClientCompanyAllowedCategoryAndProductData).display_name,
                unit_for_quantity: (newValue as ClientCompanyAllowedCategoryAndProductData).unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: '',
                },
              },
              total_quantity: '',
            })
            onTotalValueChange?.()
          }}
          isDisabled={disabled}
        />

        {entryProducts?.map((entryProduct, index) => (
          <EntryProductFormItemQuantity
            key={entryProduct?.product?.id}
            entryProductIndex={index}
            entryProduct={entryProduct as GovernmentAllocationEntryProduct}
            onDelete={() => {
              removeEntryProduct(index)
              onTotalValueChange?.()
            }}
            disabled={disabled}
            allowedProductSummary={allowedCategoriesAndProducts?.products?.[entryProduct?.product?.id]}
            onTotalValueChange={onTotalValueChange}
            governmentAllocationCurrencyRestricted={governmentAllocationCurrencyRestricted}
          />
        ))}
      </Box>
    </div>
  )
}
